import { INPUT_BUTTONS, type Aabb, type InputMove, type MovementConfig, type Vec2 } from "@townsy/contracts";
import { moveWithSliding, pointInsideAny } from "./collision";
import { clamp, wrapAngle, yawFromDirection } from "./geometry";
import type { ColliderSet } from "./spatialIndex";
import { circleIntersectsObb, pushCircleOutOfObb, type Obb } from "./vehicles";

/** Authoritative kinematic state of one actor on the ground plane. */
export interface MotionState {
  x: number;
  z: number;
  facing: number;
  /** 0 = idle, 1 = walking, 2 = running (ACTOR_ANIM). Presentation hint only. */
  anim: 0 | 1 | 2;
}

/**
 * Running (founder request 2026-10-13, ADR-0018): while the `run` bit of `InputMove.buttons` is set,
 * a step covers RUN_SPEED_MULTIPLIER x the walking distance (walk 3.5 m/s -> run 6.125 m/s). The
 * manifest's `movement` object is unchanged (the walking speed); the server and the client's
 * prediction both derive the running speed here, so prediction never disagrees with the server.
 */
export const RUN_SPEED_MULTIPLIER = 1.75;

/** The input fields stepMotion reads (`buttons` is optional: AI residents and older callers walk). */
export type MotionInput = Pick<InputMove, "moveX" | "moveZ" | "facing"> & { buttons?: number };

/** True when the input asks to run (the `run` bit; every other bit is ignored, malformed values walk). */
export function isRunInput(buttons: number | undefined): boolean {
  return typeof buttons === "number" && Number.isInteger(buttons) && buttons >= 0 && (buttons & INPUT_BUTTONS.run) !== 0;
}

/** Ground speed (m/s) for an input: walking, or RUN_SPEED_MULTIPLIER x walking with the run bit. */
export function moveSpeed(cfg: Pick<MovementConfig, "walkSpeedMetresPerSecond">, buttons: number | undefined): number {
  return isRunInput(buttons) ? cfg.walkSpeedMetresPerSecond * RUN_SPEED_MULTIPLIER : cfg.walkSpeedMetresPerSecond;
}

/** Clamp a movement vector to unit length so diagonal input cannot exceed walking speed. */
export function normalizeMove(mx: number, mz: number): Vec2 {
  if (!Number.isFinite(mx) || !Number.isFinite(mz)) return { x: 0, z: 0 };
  const len = Math.hypot(mx, mz);
  if (len <= 1e-6) return { x: 0, z: 0 };
  const s = len > 1 ? 1 / len : 1;
  return { x: mx * s, z: mz * s };
}

function insideAnyObb(x: number, z: number, radius: number, obstacles: readonly Obb[]): boolean {
  for (const o of obstacles) if (circleIntersectsObb(x, z, radius, o)) return true;
  return false;
}

function blockedAt(p: Vec2, radius: number, colliders: ColliderSet, obstacles: readonly Obb[], bounds: Aabb): boolean {
  if (p.x < bounds.minX + radius - 1e-9 || p.x > bounds.maxX - radius + 1e-9) return true;
  if (p.z < bounds.minZ + radius - 1e-9 || p.z > bounds.maxZ - radius + 1e-9) return true;
  return pointInsideAny(p.x, p.z, radius, colliders) || insideAnyObb(p.x, p.z, radius, obstacles);
}

/** Push a circle out of every oriented box it overlaps (a few passes for boxes that touch each other). */
function resolveObbs(p: Vec2, radius: number, obstacles: readonly Obb[], bounds: Aabb): Vec2 {
  let x = p.x;
  let z = p.z;
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (const o of obstacles) {
      const out = pushCircleOutOfObb(x, z, radius, o);
      if (!out) continue;
      x = clamp(out.x, bounds.minX + radius, bounds.maxX - radius);
      z = clamp(out.z, bounds.minZ + radius, bounds.maxZ - radius);
      moved = true;
    }
    if (!moved) break;
  }
  return { x, z };
}

/**
 * Nearest free spot around `p` (rings of 0.25 m, 16 directions, up to 4 m): used only when an
 * actor is already inside an obstacle (a car stopped on them, a spawn) and cannot be pushed out
 * directly because a static box is in the way.
 */
function nearestFree(p: Vec2, radius: number, colliders: ColliderSet, obstacles: readonly Obb[], bounds: Aabb): Vec2 | null {
  for (let ring = 1; ring <= 16; ring++) {
    const d = ring * 0.25;
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * Math.PI * 2;
      const c = { x: p.x + Math.sin(a) * d, z: p.z + Math.cos(a) * d };
      if (!blockedAt(c, radius, colliders, obstacles, bounds)) return c;
    }
  }
  return null;
}

/**
 * Advance one actor by one input over `dtSeconds`. Pure and deterministic: the server runs it
 * authoritatively and the client runs the same function for prediction and reconciliation.
 *
 * `colliders` are static axis-aligned boxes (the actor slides along them): a plain list, or a
 * ColliderIndex built from the same list (buildColliderIndex; identical result, cost independent of
 * the size of the town). `obstacles` are oriented
 * boxes such as cars (ADR-0006): after the slide the actor is pushed out of any it overlaps, so it
 * slides along a car's side and can never end inside one. If that push would put it inside a static
 * box it stays where it was; if it was already inside a car (the car stopped on it, a spawn) it is
 * moved to the nearest free spot.
 *
 * The `run` bit of `input.buttons` (ADR-0018) multiplies the speed by RUN_SPEED_MULTIPLIER and
 * reports anim 2; without it the step is exactly the walking step.
 */
export function stepMotion(
  state: MotionState,
  input: MotionInput,
  dtSeconds: number,
  cfg: Pick<MovementConfig, "walkSpeedMetresPerSecond" | "actorRadiusMetres">,
  colliders: ColliderSet,
  bounds: Aabb,
  obstacles: readonly Obb[] = [],
): MotionState {
  const dt = Math.min(Math.max(dtSeconds, 0), 0.25); // never let a stall create extra distance
  const move = normalizeMove(input.moveX, input.moveZ);
  const moving = move.x !== 0 || move.z !== 0;
  const running = moving && isRunInput(input.buttons);
  const speed = moveSpeed(cfg, input.buttons);
  const radius = cfg.actorRadiusMetres;
  const delta = { x: move.x * speed * dt, z: move.z * speed * dt };
  let next = moving ? moveWithSliding({ x: state.x, z: state.z }, delta, radius, colliders, bounds) : { x: state.x, z: state.z };
  if (obstacles.length && insideAnyObb(next.x, next.z, radius, obstacles)) {
    const pushed = resolveObbs(next, radius, obstacles, bounds);
    const start = { x: state.x, z: state.z };
    if (!blockedAt(pushed, radius, colliders, obstacles, bounds)) next = pushed;
    else if (!insideAnyObb(start.x, start.z, radius, obstacles)) next = start;
    else next = nearestFree(start, radius, colliders, obstacles, bounds) ?? pushed;
  }
  const facing = moving ? yawFromDirection(move.x, move.z) : wrapAngle(Number.isFinite(input.facing) ? input.facing : state.facing);
  return { x: next.x, z: next.z, facing, anim: running ? 2 : moving ? 1 : 0 };
}

export const IDLE_INPUT: MotionInput = { moveX: 0, moveZ: 0, facing: 0 };
